import { useContext } from 'react';
import clsx from 'clsx';
import { useHistory, useLocation } from 'react-router-dom';
import { RECIPE_COUNT_DISPLAY } from '@hungryroot/constants';
import { useAndroidWebViewHeightFix } from '@hungryroot/environment';
import {
  Button,
  Dimension,
  Heading,
  Paragraph,
  BuyMoreSaveMore,
  ImproveHealth,
  WasteLessFood,
  BreakpointSizeMedium,
} from '@hungryroot/ui-kit';
import { isFoodPreferencesIntroDirectEntry } from '../../defs/foodPreferencesIntroEntry';
import { QuizContext } from '../../quiz-context';
import { Footer } from '../Footer/Footer';
import styles from './FoodPreferencesIntro.module.css';

/** Follows a quiz step, so it reads as the next one. */
const HEADING = 'Next, tell us what looks good';
/** A guest sent here from login or a CTA has no preceding step. */
const HEADING_DIRECT_ENTRY = 'Tell us what looks good';

export const FOOD_PREFERENCES_INTRO_DESKTOP_IMAGE_URL =
  '/public/img/quiz/foodPreferencesIntroDesktop.webp';
export const FOOD_PREFERENCES_INTRO_MOBILE_IMAGE_URL =
  '/public/img/quiz/foodPreferencesIntroMobile.webp';

// `style` here is Ramen Button's variant prop (a string), not a CSS style;
// hoisted out of JSX so house/no-inline-styles doesn't misfire on it.
const BACK_BUTTON_PROPS = {
  style: 'accent',
  accentColor: 'avocado',
  size: 'medium',
  prominence: 'secondary',
} as const;

const FUNNEL_CONTINUE_BUTTON_PROPS = {
  style: 'accent',
  accentColor: 'avocado',
  size: 'medium',
  prominence: 'primary',
} as const;

const MINI_QUIZ_CONTINUE_BUTTON_PROPS = {
  ...FUNNEL_CONTINUE_BUTTON_PROPS,
  width: 'full-width',
} as const;

interface Props {
  testid?: string;
  next: string | null;
  prev: string | null;
}

export function FoodPreferencesIntro({ testid, next, prev }: Props) {
  const history = useHistory();
  const { search } = useLocation();
  const { isMiniQuiz } = useContext(QuizContext);
  useAndroidWebViewHeightFix();

  const heading = isFoodPreferencesIntroDirectEntry(search)
    ? HEADING_DIRECT_ENTRY
    : HEADING;

  return (
    <div
      data-testid={testid}
      className={clsx(styles.container, {
        [styles['container-mini-quiz']]: isMiniQuiz,
      })}
    >
      <picture className={styles.picture}>
        <source
          srcSet={FOOD_PREFERENCES_INTRO_DESKTOP_IMAGE_URL}
          media={`(min-width: ${BreakpointSizeMedium})`}
          width={2017}
          height={1255}
        />
        <img
          src={FOOD_PREFERENCES_INTRO_MOBILE_IMAGE_URL}
          alt=""
          width={800}
          height={1200}
          fetchPriority="high"
          loading="eager"
        />
      </picture>
      <div className={styles.content}>
        <Heading variant="HeroAlt" className={styles.heading}>
          {heading}
        </Heading>
        <ul className={styles.list}>
          <li className={styles.item}>
            <WasteLessFood className={styles.icon} />
            <Paragraph variant="lead">
              We've curated options for you from over {RECIPE_COUNT_DISPLAY}{' '}
              groceries and recipes.
            </Paragraph>
          </li>
          <li className={styles.item}>
            <ImproveHealth className={styles.icon} />
            <Paragraph variant="lead">
              Your selections allow us to better tailor our recommendations.
            </Paragraph>
          </li>
          <li className={styles.item}>
            <BuyMoreSaveMore className={styles.icon} />
            <Paragraph variant="lead">
              You can shop our full catalog after checkout.
            </Paragraph>
          </li>
        </ul>
      </div>
      <Dimension
        level={isMiniQuiz ? 'none' : 'four'}
        as={Footer}
        floating={isMiniQuiz}
        className={clsx(styles.footer, {
          [styles['footer-floating']]: isMiniQuiz,
        })}
        backButton={
          !isMiniQuiz &&
          prev !== null && (
            <Button
              {...BACK_BUTTON_PROPS}
              onClick={(e) => {
                e.preventDefault();
                history.push(prev);
              }}
            >
              Back
            </Button>
          )
        }
        submitButton={
          <Button
            {...(isMiniQuiz
              ? MINI_QUIZ_CONTINUE_BUTTON_PROPS
              : FUNNEL_CONTINUE_BUTTON_PROPS)}
            buttonType="submit"
            testid="quiz-button-next"
            onClick={() => {
              if (next !== null) {
                history.push(next);
              }
            }}
          >
            Continue
          </Button>
        }
      />
    </div>
  );
}
